import {h, Fragment} from "preact"
import { useResponse, AutocompleteElement, Keywords, useAppState } from "@nosto/preact"
import { useEffect, useRef, useState } from "preact/hooks"
import DropdownProduct from "./DropdownProduct"
import { closeSearch, submitSearch } from "../helpers"
import { searchRefColors } from "../config"

export default function Autocomplete() {
    const { products, keywords } = useResponse();
    if (!products?.hits?.length && !keywords?.hits?.length) return
    const { query } = useAppState();
    const autocompleteRef = useRef(null);
    const [ topDistance, setTopDistance ] = useState(0);
    const [ colorInQuery, setColorInQuery ] = useState(null);   
    const timeoutRef = useRef(null);

    useEffect(() => {
        if(autocompleteRef.current) {
            const curTopDistance = autocompleteRef.current.getBoundingClientRect().top;
            if(curTopDistance !== topDistance && curTopDistance !== 0) {
                autocompleteRef.current.style.setProperty('--top-distance', curTopDistance + 'px')
                setTopDistance(curTopDistance);
            }
        }
    }, [ autocompleteRef ])

    useEffect(() => {
        if(typeof window._swat?.initializeActionButtons === 'function') {
            _swat.initializeActionButtons('.ns-suggested .ns-product-list', '[ns-wishlist-btn]');
        }
    }, [ products?.hits ]);

    useEffect(() => {
        if(!query?.query) return;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            const lowerCasedQuery = query.query.toLowerCase();
            const foundColorsInQuery = searchRefColors.filter(color => lowerCasedQuery.includes(color));
            if(query.query.length < 3 || (foundColorsInQuery.length === 0 && colorInQuery)) {
                setColorInQuery(null)
            } else if(foundColorsInQuery.length === 1 && foundColorsInQuery[0] !== colorInQuery) {
                setColorInQuery(foundColorsInQuery[0])
            } else if (foundColorsInQuery.length > 1) {
                let firstColorMatch = null;
                let firstIndex = lowerCasedQuery.length;
                for(const color of searchRefColors) {
                    const index = lowerCasedQuery.indexOf(color);
                    if(index !== -1 && index < firstIndex) {
                        firstColorMatch = color;
                        firstIndex = index;
                    }
                }
                if(firstColorMatch === colorInQuery) return;
                setColorInQuery(firstColorMatch);
            }
        }, 750);
        return () => clearTimeout(timeoutRef.current);
    }, [ query?.query ])

    return (
        <div
            class="ns-autocomplete ns-suggested"
            data-nosto-element="autocomplete"
            ref={autocompleteRef}
        >
            <div class="ns-autocomplete-inner ns-flex ns-flex-col ns-lg-flex-row">

                <div class="ns-autocomplete-section ns-keywords-section ns-flex ns-flex-nowrap ns-lg-flex-col ns-lg-flex-wrap">
                    {/* SUGGESTIONS */}
                    {keywords?.hits?.length > 0 && (
                        <div class="ns-related-searches">
                            <div class="ns-section-header">
                                <h4 class="ns-section-heading">
                                    Related Searches
                                </h4>
                            </div>
                            <ul class="ns-keyword-list ns-flex ns-flex-col list-unstyled">
                                <Keywords
                                    hits={keywords.hits}
                                    keywordComponent={Keyword}
                                    elementComponent={Element}
                                    keywordInCategoryComponent={KeywordInCategory}
                                />
                            </ul>
                        </div>
                    )}
                </div>

                {/* PRODUCTS */}
                {products?.hits?.length > 0 && (
                    <div class="ns-autocomplete-section ns-products-section">
                        <div class="ns-section-header ns-flex ns-flex-nowrap ns-items-center ns-justify-between">
                                <h4 class="ns-section-heading">
                                Related Products
                            </h4>
                            <button 
                                class="ns-autocomplete__results ns-flex ns-flex-nowrap ns-items-center ns-justify-center ns-mobile-only" 
                                aria-label="View results" 
                                type="submit"
                                onClick={submitSearch}
                            >
                                <span class="ns-autocomplete__results-text">
                                    {products.total} {products.total === 1 ? 'Result' : 'Results'}
                                </span>
                            </button>
                        </div>
                        <ul class="ns-product-list ns-flex ns-flex-wrap list-unstyled">
                            {products?.hits?.map((hit, index) => (
                                <DropdownProduct
                                    key={hit.productId || index}
                                    product={hit}
                                    colorInQuery={colorInQuery}
                                />
                            ))}
                        </ul>
                        <button 
                            class="ns-autocomplete__view-results ns-flex ns-flex-nowrap ns-items-center ns-justify-center" 
                            aria-label="View results" 
                            type="submit"
                            onClick={() => {
                                setTimeout(() => {
                                    closeSearch();
                                }, (window.innerWidth < 990) ? 500 : 0)
                            }}
                        >
                            <span class="ns-autocomplete__view-results-text">
                                View {products.total} {products.total === 1 ? 'Result' : 'Results'}
                            </span>
                            <svg aria-hidden="true" focusable="false" role="presentation" class="ns-autocomplete__view-results-icon" viewBox="0 0 10 6">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                            </svg>
                        </button>
                    </div>
                )}

            </div>
        </div>
    )
}

function Keyword({ hit }) {
    return (
        <span data-nosto-element="keyword">
            {hit?._highlight?.keyword ? (
                <span
                    dangerouslySetInnerHTML={{
                        __html: hit._highlight.keyword
                    }}
                ></span>
            ) : (
                hit.keyword
            )}
        </span>
    )
}

function Element({ hit, keywordComponent, filter }) {
    return (
        <li>
            <AutocompleteElement hit={hit} filter={filter} class="ns-keyword-item">
                {keywordComponent}
            </AutocompleteElement>
        </li>
    )
}

function KeywordInCategory({ category, children }) {
    return (
        <>
            {category?.value ? (
                <>
                    {children}{" "}
                    <span class="ns-keyword-in-category">
                        in <span>{category.value}</span>
                    </span>
                </>
            ) : (
                children
            )}
        </>
    )
}
